import { fontFamilyNameSchema, MAX_FONT_FAMILY_NAME_LENGTH } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { koreanMessageOr } from '@/shared/lib';
import { Button, CheckboxField, FileDropField, Input, Label } from '@/shared/ui';
import { checkFontFile, familyNameFromFileName } from '../model/font-file';
import type { CreateFontPhase } from '../model/use-create-font';

const ACCEPT = '.woff2,.ttf,.otf';
const NAME_HINT = `폰트 이름은 1~${MAX_FONT_FAMILY_NAME_LENGTH}자로 입력해 주세요.`;
const PHASE_LABELS: Readonly<Record<CreateFontPhase, string | null>> = {
  idle: null,
  uploading: '파일 올리는 중…',
  registering: '등록 확인 중…',
};

interface FieldErrors {
  readonly file: string | null;
  readonly familyName: string | null;
  readonly license: string | null;
}

const NO_ERRORS: FieldErrors = { file: null, familyName: null, license: null };

/** 고른 파일의 오류 — 문제가 없으면 null. */
const fileErrorOf = (file: File | null): string | null => {
  if (file === null) return '폰트 파일을 골라 주세요.';
  const checked = checkFontFile(file);
  return checked.ok ? null : checked.message;
};

interface FontUploadModalProps {
  readonly phase: CreateFontPhase;
  /** 발급·PUT·등록 실패 — 모달 위 알림. */
  readonly errorMessage: string | null;
  /** 살아 있는 폰트와 이름이 겹친다 — 이름 칸 아래. 이름만 바꿔 다시 등록하면 올린 파일을 그대로 쓴다. */
  readonly nameErrorMessage: string | null;
  readonly onSubmit: (input: { readonly file: File; readonly familyName: string }) => void;
  readonly onClose: () => void;
}

/**
 * 폰트 추가 모달 — 파일·이름·재배포 가능 라이선스 확인(결정 F3)을 받는다. 서버와 같은 계약 스키마로 먼저 검사하고,
 * zod 기본 영어 문구는 한국어 문구로 바꿔 보인다.
 */
export const FontUploadModal = ({
  phase,
  errorMessage,
  nameErrorMessage,
  onSubmit,
  onClose,
}: FontUploadModalProps): ReactElement => {
  const [file, setFile] = useState<File | null>(null);
  const [familyName, setFamilyName] = useState('');
  const [isLicenseConfirmed, setIsLicenseConfirmed] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>(NO_ERRORS);
  const nameError = errors.familyName ?? nameErrorMessage;
  const progress = PHASE_LABELS[phase];

  const handleFileChange = (next: File | null): void => {
    setFile(next);
    // 고르기를 취소해 파일이 비면 오류를 지운다 — '골라 주세요'는 등록할 때만 보인다.
    setErrors((current) => ({ ...current, file: next === null ? null : fileErrorOf(next) }));

    if (next !== null && familyName.trim() === '') {
      setFamilyName(familyNameFromFileName(next.name));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const fileError = fileErrorOf(file);
    const parsedName = fontFamilyNameSchema.safeParse(familyName);

    setErrors({
      file: fileError,
      familyName: parsedName.success
        ? null
        : koreanMessageOr(parsedName.error.issues[0]?.message, NAME_HINT),
      license: isLicenseConfirmed ? null : '재배포 가능한 라이선스인지 확인해 주세요.',
    });

    if (file !== null && fileError === null && parsedName.success && isLicenseConfirmed) {
      onSubmit({ file, familyName: parsedName.data });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="폰트 추가"
        className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-lg border bg-background p-5"
      >
        <p className="font-medium">폰트 추가</p>

        {errorMessage !== null ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="font-upload-file">폰트 파일</Label>
            <FileDropField
              id="font-upload-file"
              accept={ACCEPT}
              buttonLabel="폰트 파일 선택"
              fileName={file?.name ?? null}
              aria-invalid={errors.file !== null}
              aria-describedby={errors.file !== null ? 'font-upload-file-error' : undefined}
              onFiles={([next]) => handleFileChange(next ?? null)}
            />
            <p className="text-xs text-muted-foreground">
              woff2·ttf·otf, 10MB 이하 — 한글 전체 글꼴은 woff2 를 권장합니다.
            </p>
            {errors.file !== null ? (
              <p id="font-upload-file-error" className="text-xs text-destructive">
                {errors.file}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="font-upload-name">폰트 이름</Label>
            <Input
              id="font-upload-name"
              value={familyName}
              maxLength={MAX_FONT_FAMILY_NAME_LENGTH}
              placeholder="예: 나눔고딕"
              onChange={(event) => setFamilyName(event.target.value)}
              hasError={nameError !== null}
              aria-describedby={nameError !== null ? 'font-upload-name-error' : undefined}
            />
            {nameError !== null ? (
              <p id="font-upload-name-error" className="text-xs text-destructive">
                {nameError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5 rounded-md bg-muted/50 p-3">
            <p className="text-xs text-muted-foreground">
              폰트 파일은 로그인하지 않은 고객의 에디터에도 그대로 내려갑니다. 재배포가 허용된
              폰트(OFL 등)만 올려 주세요.
            </p>
            <CheckboxField
              label="재배포가 허용된 라이선스임을 확인했습니다."
              checked={isLicenseConfirmed}
              onChange={(event) => setIsLicenseConfirmed(event.target.checked)}
              error={errors.license ?? undefined}
            />
          </div>

          {/* live region 은 늘 두고 글자만 바꾼다 — 내용과 함께 새로 끼우면 첫 단계 안내를 화면 낭독기가 대개 읽지 않는다. */}
          <p
            aria-live="polite"
            className={progress === null ? 'sr-only' : 'text-sm text-muted-foreground'}
          >
            {progress}
          </p>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" size="sm" isLoading={phase !== 'idle'}>
              등록하기
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
